<script setup lang="ts">
import {computed, onBeforeUnmount, onMounted, ref, watch} from "vue";
import { Search, ArrowDownBold, ArrowUpBold } from '@element-plus/icons-vue';
import FiltersDrawer from "@/components/FiltersDrawer/FiltersDrawer.vue";
import SearchDropdownFilter from "@/components/SearchResults/filters/SearchDropdownFilter.vue";
import SearchSliderFilter from "@/components/SearchResults/filters/SearchSliderFilter.vue";
import SearchNumberFilter from "@/components/SearchResults/filters/SearchNumberFilter.vue";
import {useBodyScrollLock} from "@/composables/useBodyScrollLock";
import {useRoute, useRouter} from "vue-router";
import {useSearchStore} from "@/stores/searchStore";
import {useYachtStore} from "@/stores/yachtStore";

const searchStore = useSearchStore();
const yachtStore = useYachtStore();

const route = useRoute()
const router = useRouter()

// Dropdown
const isCategoryOpen = ref(false)
const isDestinationOpen = ref(false)
const isLengthOpen = ref(false)
const isPriceOpen = ref(false)
const isSortOpen = ref(false)


const searchActive = ref(false)
const searchRef = ref(null)


const removeCategory = (cat: string) => {
  searchStore.categories = searchStore.categories.filter(c => c !== cat)
}

// const removeDestination = (dest: string) => {
//   searchStore.destinations = searchStore.destinations.filter(d => d !== dest)
// }

const clearLength = () => {
  searchStore.length = [searchStore.minLength, searchStore.maxLength]
}

const clearPrice = () => {
  searchStore.price = [searchStore.minPrice, searchStore.maxPrice]
}

const clearGuests = () => {
  searchStore.guests = 1
}

const activateSearch = () => {
  searchActive.value = true
}

const deactivateSearch = () => {
  searchActive.value = false
}

const handleClickOutside = (event: MouseEvent) => {
  const hasInput = searchStore.searchQuery.trim().length > 0;
  if (searchRef.value && !searchRef.value.contains(event.target) && !hasInput) {
    deactivateSearch();
  }
}


// Register and clean up event listener
onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})

const openFilterModal = ref(false);
useBodyScrollLock(openFilterModal)

const filteredYachtsByName = computed(() => {
  if (!searchStore.searchQuery.trim()) return searchStore.yachts;

  return searchStore.yachts.filter((yacht) =>
      yacht.name?.toLowerCase().includes(searchStore.searchQuery.toLowerCase())
  );
});


onMounted(() => {
  searchStore.setFromQuery(route.query)
})


watch(
    () => searchStore.query,
    (newQuery) => {
      router.replace({ query: newQuery })
    },
    { deep: true }
)


</script>

<template>

  <!-- Mobile Filter Button -->
<div class="space-y-3">
  <div class="lg:hidden bg-background-beige shadow px-4 py-2 space-y-3">
    <div class="container flex justify-between items-center">
      <!-- Search Title -->
      <div class="font-semibold responsive-title-5 text-bw-midGrey lg:hidden">
        Search
      </div>
      <!-- Filter Button -->
      <button
          class="border transition-[border-color] cursor-pointer h-7.5 px-5 rounded-12xl flex gap-2 items-center relative"
          @click="openFilterModal = !openFilterModal"
      >
        <img src="@/assets/icons/Filters_16x16.svg" alt="filters"/>
      </button>
    </div>
  </div>
  <div class="lg:hidden px-4 py-2 space-y-3">
    <div class="container flex justify-between items-center">
      <div class="block sm:hidden responsive-body-large text-bw-midGrey">{{searchStore.filteredYachts.length}} results</div>
      <div class="hidden sm:block"></div>
      <!-- Sort Dropdown -->
      <el-dropdown trigger="click" @visible-change="val => isSortOpen = val">
            <span class="el-dropdown-link flex items-center cursor-pointer">
              <span class="sort-label text-bw-midGrey font-semibold">
                Sort by: {{ searchStore.selectedSort }}
              </span>
              <el-icon class="el-icon--right ml-1">
                <component :is="isSortOpen ? ArrowUpBold : ArrowDownBold" />
              </el-icon>
            </span>
        <template #dropdown>
          <el-dropdown-menu>
            <el-dropdown-item
                v-for="option in searchStore.sortOptions"
                :key="option"
                @click="searchStore.selectedSort = option"
            >
              {{ option }}
            </el-dropdown-item>
          </el-dropdown-menu>
        </template>
      </el-dropdown>
    </div>
  </div>
</div>

  <div class="container hidden lg:flex flex-row justify-between items-center w-full gap-4 px-6 sm:px-25 py-8 z-10 -mt-8 bg-background-beige border shadow-xl relative">

    <!-- Left: Filters - Desktop -->
    <div class="flex flex-row gap-10 items-center">

      <!-- Category -->
      <SearchDropdownFilter
          title="Category"
          v-model="searchStore.categories"
          :options="searchStore.categoryOptions"
          :isOpen="isCategoryOpen"
      />

<!--      Destination -->
<!--      <SearchDropdownFilter-->
<!--          title="Destination"-->
<!--          v-model="searchStore.destinations"-->
<!--          :options="searchStore.destinationOptions"-->
<!--          :isOpen="isDestinationOpen"-->
<!--      />-->

      <!-- Length -->
      <SearchSliderFilter
          title="Length"
          v-model="searchStore.length"
          :isOpen="isLengthOpen"
          :min_value="searchStore.minLength"
          :max_value="searchStore.maxLength"
          :step="1"
      />

      <SearchNumberFilter
          title="Guests"
          v-model="searchStore.guests"
          :isOpen="isLengthOpen"
      />

      <!-- Price -->
      <SearchSliderFilter
          title="Price"
          v-model="searchStore.price"
          :isOpen="isPriceOpen"
          :min_value="searchStore.minPrice"
          :max_value="searchStore.maxPrice"
          :step="5000"
      />
    </div>

    <!-- Right: Search + Sort -->
    <div class="flex flex-row items-center gap-12">
      <!-- Expandable Search -->
      <div class="relative">
        <div
            ref="searchRef"
            class="relative transition-all duration-300 ease-in-out flex items-center"
            :class="{ 'w-64': searchActive, 'w-32': !searchActive }"
            @click.stop="activateSearch"
        >
          <el-input
              v-if="searchActive"
              v-model="searchStore.searchQuery"
              placeholder="Search by name"
              size="large"
              class="w-full bg-background-light"
              clearable
          >
            <template #prefix>
              <el-icon><Search /></el-icon>
            </template>
          </el-input>

          <div
              v-else
              class="flex items-center text-primary-violet hover:text-bw-midGrey font-bold responsive-body-xlarge cursor-pointer space-x-2 px-2 py-1"
          >
            <el-icon><Search /></el-icon>
            <span>Search</span>
          </div>
        </div>
        <div v-if="searchActive && searchStore.searchQuery" class="absolute left-0 top-full w-64 max-h-52 overflow-y-auto scroll-indicator bg-white shadow-lg border-t-2 z-50">

          <ul class="text-bw-midGrey responsive-body-large cursor-pointer">
            <li
                v-for="yacht in filteredYachtsByName"
                class="hover:bg-primary-violet px-5 py-3"
                @click="yachtStore.viewYachtDetails(yacht.slug)"
            >
              {{yacht.name}}
            </li>
          </ul>
        </div>
      </div>

      <div class="flex items-center h-full">
        <el-dropdown trigger="click" @visible-change="val => isSortOpen = val">
          <span class="el-dropdown-link flex items-center">
            Sort by: {{ searchStore.selectedSort }}
            <el-icon class="el-icon--right ml-1">
              <component :is="isSortOpen ? ArrowUpBold : ArrowDownBold" />
            </el-icon>
          </span>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item
                v-for="option in searchStore.sortOptions"
                :key="option"
                @click="searchStore.selectedSort = option"
              >{{ option }}

              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </div>
    </div>
  </div>


  <!-- Selected Filters Display - Desktop -->
  <div
      v-if="searchStore.categories.length || searchStore.guests > 1 || searchStore.isLengthFiltered || searchStore.isPriceFiltered"
      class="container flex-wrap items-center hidden sm:flex gap-4 py-2 mt-4 border-l-8 border-primary-violet"
  >
    <div class="flex flex-wrap items-center flex-row gap-3 pl-5">
      <div class="responsive-body-medium text-primary-darkBlue font-bold">
        Showing {{searchStore.filteredYachts.length}} results
      </div>
      <!-- Category -->
      <template v-if="searchStore.categories.length > 0">
        <div
             v-for="cat in searchStore.categories"
             class="text-bw-midGrey hover:text-white hover:bg-bw-midGrey responsive-body-medium border-2 border-bw-midGrey rounded-md py-1 px-2 lg:py-2 lg:px-4 cursor-pointer"
             :key="'cat-' + cat"
             @click="removeCategory(cat)"
        >
          {{ cat }}
          <button class="ml-2 text-primary-violet hover:text-white font-bold">×</button>
        </div>
      </template>

      <!-- Destination -->
<!--      <template v-if="searchStore.destinations.length > 0">-->
<!--        <div-->
<!--             v-for="dest in searchStore.destinations"-->
<!--             :key="'dest-' + dest"-->
<!--             class="text-bw-midGrey hover:text-white hover:bg-bw-midGrey responsive-body-medium border-2 border-bw-midGrey rounded-md py-1 px-2 lg:py-2 lg:px-4  cursor-pointer"-->
<!--             @click="removeDestination(dest)"-->
<!--        >-->
<!--          {{ dest }}-->
<!--          <button class="ml-2 text-primary-violet hover:text-white font-bold">×</button>-->
<!--        </div>-->
<!--      </template>-->

      <!-- Length -->
      <template v-if="searchStore.isLengthFiltered">
        <div
            class="text-bw-midGrey hover:text-white hover:bg-bw-midGrey responsive-body-medium border-2 border-bw-midGrey rounded-md py-1 px-2 lg:py-2 lg:px-4 cursor-pointer"
            @click="clearLength"
        >
          {{ searchStore.length[0] }} - {{ searchStore.length[1] }}m length
          <button class="ml-2 text-primary-violet hover:text-white font-bold">×</button>
        </div>
      </template>

      <!-- Guests -->
      <template v-if="searchStore.guests > 1">
        <div class="text-bw-midGrey hover:text-white hover:bg-bw-midGrey responsive-body-medium border-2 border-bw-midGrey rounded-md py-1 px-2 lg:py-2 lg:px-4 cursor-pointer" @click="clearGuests">
          Guests: {{ searchStore.guests }}
          <button class="ml-2 text-primary-violet hover:text-white  font-bold">×</button>
        </div>
      </template>

      <!-- Price -->
      <template v-if="searchStore.isPriceFiltered">
        <div
            class="text-bw-midGrey hover:text-white hover:bg-bw-midGrey responsive-body-medium border-2 border-bw-midGrey rounded-md py-1 px-2 lg:py-2 lg:px-4 cursor-pointer"
            @click="clearPrice"
        >
          {{ searchStore.price[0] }} - {{ searchStore.price[1] }} &euro;
          <button class="ml-2 text-primary-violet hover:text-white font-bold">×</button>
        </div>
      </template>

      <!-- Clear All Button -->
      <div
          class="responsive-body-medium border-2 border-primary-violet bg-primary-violet hover:bg-transparent rounded-md py-1 px-2 lg:py-2 lg:px-4 cursor-pointer flex items-center group"
          @click="searchStore.clearAllFilters"
      >
        <span class="text-white group-hover:!text-primary-violet">Clear All</span>
        <button class="ml-2 text-white group-hover:!text-bw-midGrey font-bold">×</button>
      </div>

    </div>
  </div>

  <!-- Mobile -->
  <FiltersDrawer :openFilterModal="openFilterModal" @close="openFilterModal = false" />

</template>

<style scoped>

:deep(.el-dropdown-menu__item){
  font-size: 20px;
  margin-bottom: 5px;
  color: #676188;
  --el-backtop-hover-bg-color: #676188;
}

@media (max-width: 768px) {
  :deep(.el-dropdown-menu__item){
    font-size: 15px;
    color: #676188 !important;
  }
}

:deep(.el-input__inner) {
  font-size: 25px;
  height: 70px;
  line-height: 48px;
  padding-top: 0;
  padding-bottom: 0;
  color: #beA5aa;
  --el-input-placeholder-color: #beA5aa;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}


</style>
